import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import {
  Video, X, Loader2, CheckCircle, AlertCircle, Play, Download, Eye,
  Upload as UploadIcon, Dice5, Lock, Unlock, ChevronDown,
} from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

// Seedance 1 Pro accepted values
const DURATIONS = [5, 10];
const RESOLUTIONS = ['480p', '1080p'];
const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'];

// Quick prompt seeds — non-prescriptive starting points users can tweak.
const PROMPT_SUGGESTIONS = [
  'Movimiento cinematográfico suave con luz natural',
  'Zoom lento hacia el sujeto principal',
  'Cámara orbita alrededor del sujeto',
  'Movimiento de dron sobre el paisaje',
  'Aire que ondea ropa y cabello',
];

// Rough cost estimate for the user (visual hint, not an authoritative quote).
const estimateMinutes = (duration, resolution) => {
  const base = duration === 10 ? 4 : 2;
  const resMul = resolution === '1080p' ? 1.5 : 1;
  return Math.round(base * resMul);
};

const VideoGenerationNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [negativePrompt, setNegativePrompt] = useState(data.negativePrompt || '');
  const [duration, setDuration] = useState(data.duration || 5);
  const [resolution, setResolution] = useState(data.resolution || '1080p');
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '16:9');
  const [cameraFixed, setCameraFixed] = useState(!!data.cameraFixed);
  const [seed, setSeed] = useState(
    typeof data.seed === 'number' ? data.seed : -1
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localError, setLocalError] = useState(null);

  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);
  const deleteNode = useWorkflowStore((s) => s.deleteNode);
  const getInputForNode = useWorkflowStore((s) => s.getInputForNode);
  const addAsset = useWorkflowStore((s) => s.addAsset);
  const edges = useWorkflowStore((s) => s.edges);
  const openSourcePicker = useWorkflowStore((s) => s.openSourcePicker);

  const {
    processNode, status: execStatus, error: execError, executionId,
    reset: resetExec, insufficientCredits,
  } = useNodeExecution();
  const isProcessing =
    execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openVideoPreview } = useMediaPreview();

  const hasInput = edges.some((edge) => edge.target === id);
  const inputData = getInputForNode(id);

  // Persist setting changes so the node keeps them across reloads.
  useEffect(() => {
    updateNodeData(id, {
      prompt,
      negativePrompt,
      duration,
      resolution,
      aspectRatio,
      cameraFixed,
      seed,
    });
  }, [id, prompt, negativePrompt, duration, resolution, aspectRatio, cameraFixed, seed, updateNodeData]);

  const estimateMin = useMemo(
    () => estimateMinutes(duration, resolution),
    [duration, resolution]
  );

  const handleSourcePick = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.url) {
        updateNodeData(id, {
          galleryUrl: result.url, galleryFileName: result.fileName, galleryFile: null,
        });
      } else if (result.file) {
        updateNodeData(id, {
          galleryUrl: URL.createObjectURL(result.file),
          galleryFileName: result.file.name,
          galleryFile: result.file,
        });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker, updateNodeData]);

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((p) => p + 1), 1000);
    }
    return () => interval && clearInterval(interval);
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const fetchFileFromUrl = async (url, filename) =>
    blobToFile(url, filename || 'input_image.jpg', 'image/jpeg');

  const buildParams = useCallback(() => ({
    prompt: prompt || 'default',
    negative_prompt: negativePrompt || undefined,
    duration,
    resolution,
    aspect_ratio: aspectRatio,
    camera_fixed: cameraFixed,
    seed,
  }), [prompt, negativePrompt, duration, resolution, aspectRatio, cameraFixed, seed]);

  const handleProcess = useCallback(async () => {
    const input = getInputForNode(id);
    const effectiveInput = input || (data.galleryUrl
      ? { outputUrl: data.galleryUrl, fileName: data.galleryFileName, file: data.galleryFile }
      : null);
    if (!effectiveInput) {
      setLocalError('Conecta una imagen de entrada al nodo.');
      return false;
    }
    setLocalError(null);
    resetExec();

    const finishOk = (outputUrl, assetId = null) => {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl,
        assetId,
        outputType: 'video',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: assetId || `asset_${Date.now()}`,
        type: 'video',
        tool: 'video',
        parent: effectiveInput.fileName,
        filename: `video_${effectiveInput.fileName || 'generated'}`,
        url: outputUrl,
        prompt,
        createdAt: new Date().toISOString(),
      });
    };

    let imageFile = effectiveInput.file;

    if (!imageFile) {
      const fileUrl = effectiveInput.outputUrl || effectiveInput.preview;
      if (fileUrl?.startsWith('http')) {
        const result = await processNode({
          resultType: 'create_video',
          params: { ...buildParams(), image_url: fileUrl },
        });
        if (result?.outputUrl) { finishOk(result.outputUrl, result.assetId); return true; }
        return false;
      }
      if (fileUrl) {
        try {
          imageFile = await fetchFileFromUrl(fileUrl, effectiveInput.fileName || 'input_image.jpg');
        } catch (_err) {
          setLocalError('Error al obtener la imagen.');
          return false;
        }
      }
    }

    if (!imageFile) {
      setLocalError('No se pudo obtener la imagen. Intenta recargar la imagen de entrada.');
      return false;
    }

    const result = await processNode({
      resultType: 'create_video',
      files: [{ key: 'image_url', file: imageFile }],
      params: buildParams(),
    });
    if (result?.outputUrl) { finishOk(result.outputUrl, result.assetId); return true; }
    return false;
  }, [
    id, data.galleryUrl, data.galleryFileName, data.galleryFile,
    getInputForNode, updateNodeData, addAsset, prompt, processNode, resetExec,
    buildParams,
  ]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = (hasInput && inputData?.hasOutput) || data.galleryUrl;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('image', !!canProcess, { connected: hasInput })]);

  return (
    <div
      className={`node-container video-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}
      data-testid="video-generation-node"
    >
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="videogen-reconfigure-notice"
        />
      )}
      <Handle type="target" position={Position.Left} id="input" className="node-handle input-handle" />

      {/* Header */}
      <div className="node-header video-header">
        <div className="node-header-left">
          <Video size={16} />
          <span>Crear Video</span>
        </div>
        <button
          className="node-delete-btn"
          onClick={() => deleteNode(id)}
          data-testid="video-gen-delete"
        >
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content">
        {/* Input status */}
        <div className={`node-input-status ${canProcess ? 'connected' : 'disconnected'}`}>
          {canProcess ? (
            <>
              <CheckCircle size={12} />
              <span>{data.galleryUrl ? 'Desde galería' : 'Entrada conectada'}</span>
            </>
          ) : (
            <>
              <AlertCircle size={12} />
              <span>Sin entrada</span>
              <button
                type="button"
                className="node-load-btn"
                onClick={handleSourcePick}
                data-testid="load-btn-videogen"
              >
                <UploadIcon size={12} /> Seleccionar
              </button>
            </>
          )}
        </div>

        {/* Form fields (hidden while processing or showing result) */}
        {!isProcessing && !data.outputUrl && (
          <>
            {/* Prompt */}
            <div className="node-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Prompt</span>
                <DirectorsTrigger nodeId={id} nodeType="videoGeneration" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="videoGeneration"
                placeholder="Describe el movimiento del video... (opcional)"
                className="node-prompt-input"
                rows={2}
                testId="video-gen-prompt"
              />
              <div className="vid-prompt-chips">
                {PROMPT_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="vid-prompt-chip"
                    onClick={() => setPrompt(s)}
                    title="Usar esta idea como prompt"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div className="vid-field">
              <label className="vid-field-label">Duración</label>
              <div className="vid-segmented">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`vid-seg-btn ${duration === d ? 'is-active' : ''}`}
                    onClick={() => setDuration(d)}
                    data-testid={`vid-duration-${d}`}
                  >
                    {d}s
                  </button>
                ))}
              </div>
            </div>

            {/* Resolution */}
            <div className="vid-field">
              <label className="vid-field-label">Resolución</label>
              <div className="vid-segmented">
                {RESOLUTIONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`vid-seg-btn ${resolution === r ? 'is-active' : ''}`}
                    onClick={() => setResolution(r)}
                    data-testid={`vid-res-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Aspect ratio — pill row with thumbnails */}
            <div className="vid-field">
              <label className="vid-field-label">Formato</label>
              <div className="vid-aspect-row">
                {ASPECT_RATIOS.map((ar) => {
                  const [w, h] = ar.split(':').map(Number);
                  const max = 18;
                  const tw = w >= h ? max : Math.max(6, Math.round((w / h) * max));
                  const th = h >= w ? max : Math.max(6, Math.round((h / w) * max));
                  return (
                    <button
                      key={ar}
                      type="button"
                      className={`vid-aspect-btn ${aspectRatio === ar ? 'is-active' : ''}`}
                      onClick={() => setAspectRatio(ar)}
                      title={`Formato ${ar}`}
                      data-testid={`vid-ar-${ar}`}
                    >
                      <span
                        className="vid-aspect-thumb"
                        style={{ width: `${tw}px`, height: `${th}px` }}
                      />
                      <span className="vid-aspect-label">{ar}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Camera fixed toggle */}
            <div className="vid-field vid-field-inline">
              <label className="vid-field-label">Cámara</label>
              <button
                type="button"
                className={`vid-toggle ${cameraFixed ? 'is-on' : ''}`}
                onClick={() => setCameraFixed((v) => !v)}
                data-testid="vid-camera-fixed"
                title={cameraFixed ? 'Cámara fija (sin movimiento)' : 'Movimiento cinematográfico'}
              >
                {cameraFixed ? <Lock size={11} /> : <Unlock size={11} />}
                <span>{cameraFixed ? 'Fija' : 'Libre'}</span>
                <span className="vid-toggle-switch">
                  <span className="vid-toggle-thumb" />
                </span>
              </button>
            </div>

            {/* Advanced (negative prompt + seed) */}
            <button
              type="button"
              className="vid-advanced-toggle"
              onClick={() => setShowAdvanced((v) => !v)}
              data-testid="vid-advanced-toggle"
            >
              <ChevronDown
                size={12}
                style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
              />
              Opciones avanzadas
            </button>
            {showAdvanced && (
              <div className="vid-advanced">
                <div className="vid-field">
                  <label className="vid-field-label">Qué evitar</label>
                  <input
                    type="text"
                    className="vid-text-input"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder="Ej: texto en pantalla, movimientos bruscos…"
                    data-testid="vid-negative-prompt"
                  />
                </div>
                <div className="vid-field vid-field-inline">
                  <label className="vid-field-label">Semilla</label>
                  <div className="vid-seed-row">
                    <input
                      type="number"
                      className="vid-text-input vid-seed-input nodrag nowheel"
                      value={seed}
                      onChange={(e) => setSeed(parseInt(e.target.value || '-1', 10))}
                      title="-1 = aleatoria"
                      data-testid="vid-seed"
                    />
                    <button
                      type="button"
                      className="vid-seed-btn"
                      onClick={() => setSeed(Math.floor(Math.random() * 1_000_000_000))}
                      title="Generar semilla aleatoria"
                    >
                      <Dice5 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Cost estimate strip */}
            <div className="vid-estimate">
              <div className="vid-estimate-cell">
                <span className="vid-estimate-label">Costo</span>
                <span className="vid-estimate-value">5 créditos</span>
              </div>
              <div className="vid-estimate-cell">
                <span className="vid-estimate-label">Estimado</span>
                <span className="vid-estimate-value">~{estimateMin} min</span>
              </div>
            </div>
          </>
        )}

        {/* Processing state */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading'
                  ? 'Subiendo archivo...'
                  : execStatus === 'queued'
                  ? 'En cola...'
                  : 'Creando video...'}
              </span>
            </div>
            <p className="node-processing-hint">Este proceso puede tomar varios minutos</p>
            {executionId && (
              <div className="node-debug-info" style={{ fontSize: '9px', opacity: 0.5, marginTop: 4 }}>
                exec: {executionId}
              </div>
            )}
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <video
              src={data.outputUrl}
              controls
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'Video Generado')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="videogen-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="videogen-reconfigure-btn" />
              <button
                className="node-mini-preview-btn"
                onClick={() => openVideoPreview(data.outputUrl, 'Video Generado')}
                data-testid="video-gen-preview"
              >
                <Eye size={12} /> Ver
              </button>
              <button
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `video_${Date.now()}.mp4`); }}
                data-testid="video-gen-download"
              >
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton
                assetId={data.assetId}
                nodeId={id}
                url={data.outputUrl}
                kind="video"
                label="videogeneration"
                testIdPrefix="videogeneration-timeline"
              />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} /><span>Video creado</span>
            </div>
          </div>
        ) : (
          <div className="node-action-area">
            <button
              className="node-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="video-gen-process-btn"
            >
              <Play size={14} /> Crear Video
            </button>
            <NodeRequirements missing={runMissing} />
          </div>
        )}

        <NodeError error={execError || localError} insufficientCredits={insufficientCredits} />
      </div>

      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

VideoGenerationNode.displayName = 'VideoGenerationNode';
export default VideoGenerationNode;
